<template>
  <div class="influencing-params-card">
    <div class="influencing-params-header">
      <b-icon icon="lightning-fill" />
      <span>{{ $t('parametros-influenciadores') }}</span>
    </div>

    <div class="influencing-params-body">
      <p v-if="indicatorName" class="influencing-params-description">
        {{ $t('vincule-os-parametros-que-impactam') }} <strong>{{ indicatorName }}</strong>.
      </p>

      <div
        v-for="(param, index) in localParameters"
        :key="index"
        class="param-row"
      >
        <div class="param-name-block">
          <div class="param-name">{{ param.indicatorName }}</div>
        </div>
        <div class="param-goal-block">
          <span class="param-goal-label">{{ $t('meta-aderencia') }}</span>
          <div class="param-goal-input-row">
            <b-form-input
              type="number"
              size="sm"
              v-model.number="param.adherenceGoal"
              :min="0"
              :max="100"
              :state="isGoalValid(param.adherenceGoal)"
              @change="emitUpdate"
              class="text-center param-goal-input"
            />
            <span class="param-percent">%</span>
          </div>
        </div>
        <span
          class="remove-param-btn"
          @click="removeParameter(index)"
          :title="$t('remover-parametro')"
        >✕</span>
      </div>

      <button class="btn-vincular" @click="openModal">
        + {{ $t('vincular-novo-parametro') }}
      </button>
    </div>

    <b-modal
      id="indicatorTreeModal"
      size="lg"
      hide-footer
      :title="$t('selecionar-parametro-influenciador')"
      :no-close-on-backdrop="true"
    >
      <IndicatorTreeModal
        :indicators="indicatorList"
        @select="onParameterSelected"
        @cancel="$bvModal.hide('indicatorTreeModal')"
      />
    </b-modal>
  </div>
</template>

<script>
import IndicatorTreeModal from './IndicatorTreeModal.vue'

export default {
  name: 'TriggerInfluencingParameters',

  components: { IndicatorTreeModal },

  props: {
    parameters: {
      type: Array,
      default: () => []
    },
    indicatorConfiguration: {
      type: Array,
      default: () => []
    },
    dicIndicator: {
      type: Object,
      default: () => ({})
    },
    indicatorName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      localParameters: []
    }
  },

  computed: {
    indicatorList() {
      return Object.values(this.dicIndicator).map(ind => ({
        indicatorId: ind.id,
        indicatorName: ind.indicatorName,
        productionUnitId: ind.productionUnitId,
        system: ind.system,
        treeLevel: 0
      }))
    }
  },

  watch: {
    parameters: {
      immediate: true,
      handler(val) {
        this.localParameters = val ? val.map(p => ({ ...p })) : []
      }
    }
  },

  methods: {
    openModal() {
      this.$bvModal.show('indicatorTreeModal')
    },

    onParameterSelected({ indicatorId, indicatorName }) {
      const alreadyAdded = this.localParameters.some(p => p.indicatorId === indicatorId)
      if (alreadyAdded) {
        this.$bvModal.hide('indicatorTreeModal')
        return
      }

      const indConfig = Array.isArray(this.indicatorConfiguration)
        ? this.indicatorConfiguration.find(ic => ic.indicatorId === indicatorId)
        : null

      const adherenceGoal = indConfig ? indConfig.reabilityThreshold : 0

      this.localParameters.push({
        id: 0,
        triggerConfigurationId: 0,
        indicatorId,
        indicatorName,
        adherenceGoal
      })

      this.$bvModal.hide('indicatorTreeModal')
      this.emitUpdate()
    },

    removeParameter(index) {
      this.localParameters.splice(index, 1)
      this.emitUpdate()
    },

    isGoalValid(value) {
      if (value === null || value === undefined || value === '') return null
      return value >= 0 && value <= 100
    },

    emitUpdate() {
      this.$emit('update', this.localParameters)
    },

    reset() {
      this.localParameters = []
    }
  }
}
</script>

<style scoped>
.influencing-params-card {
  border: 1px solid #17a2b8;
  border-radius: 4px;
  box-shadow: 0 0 3px rgba(23, 162, 184, 0.4);
  display: flex;
  flex-direction: column;
  max-height: var(--params-card-max-height, none);
  overflow: hidden;
}

.influencing-params-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background-color: #17a2b8;
  color: white;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.influencing-params-body {
  padding: 10px 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.influencing-params-body::-webkit-scrollbar {
  width: 4px;
}

.influencing-params-body::-webkit-scrollbar-track {
  background: transparent;
}

.influencing-params-body::-webkit-scrollbar-thumb {
  background-color: #c8c8c8;
  border-radius: 4px;
}

.influencing-params-description {
  font-size: 0.75rem;
  color: #555;
  margin-bottom: 10px;
  line-height: 1.4;
}

.param-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #e9ecef;
}

.param-name-block {
  flex: 1;
  min-width: 0;
}

.param-name {
  font-weight: 600;
  font-size: 0.78rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.param-goal-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}

.param-goal-label {
  font-size: 0.6rem;
  color: #17a2b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.param-goal-input-row {
  display: flex;
  align-items: center;
  gap: 3px;
}

.param-goal-input {
  width: 55px !important;
  height: 24px !important;
  font-size: 0.75rem !important;
  padding: 0 4px !important;
}

.param-percent {
  font-size: 0.75rem;
  color: #555;
}

.remove-param-btn {
  color: #dc3545;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.85rem;
  padding: 2px 4px;
  flex-shrink: 0;
}

.remove-param-btn:hover {
  color: #a71d2a;
}

.btn-vincular {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
  padding: 7px;
  background: transparent;
  border: 2px dashed #17a2b8;
  border-radius: 4px;
  color: #17a2b8;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
}

.btn-vincular:hover {
  background-color: #e8f8fb;
}
</style>
